import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Save, Plus, Trash2, ArrowUp, ArrowDown, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { BALL_OPTIONS, Field } from './PipelineStageEditor';
import { errorText } from '../../lib/apiErrors';
import { createPipelineDefinitionsApi } from '../../lib/pipelineDefinitionsApi';
import { LEAD_OUTCOME_OPTIONS, leadStageOutcome } from '../../lib/leadStages';
import { stageKeyFromLabel } from '../../lib/stageKeys';

const API = process.env.REACT_APP_BACKEND_URL || '';

const OPTION_LISTS = [
    ['prospect_qualification_options', 'Prospect Qualification'],
    ['prospect_status_options', 'Prospect Status'],
    ['prospect_source_options', 'Prospect Source'],
    ['funnel_options', 'Funnel'],
    ['page_variant_options', 'Page Variant'],
    ['form_variant_options', 'Form Variant'],
    ['product_variant_options', 'Product Variant'],
    ['reason_lost_options', 'Reason Lost'],
];

const PIPELINES_LINK = '/admin/pipelines?kind=contact';
const HISTORY_LINK = `${PIPELINES_LINK}&history=1`;
const linkCls = 'underline underline-offset-2 hover:text-stone-800';
const inputCls = "px-2 py-1.5 text-sm border border-stone-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/10";
// `xl:`, as in PipelineStageEditor: the admin sidebar is in the page flow, so the card is about the viewport minus
// 456px wide and seven tracks first fit at 1280px. Below that each control stacks with its own caption.
const STAGE_GRID = 'xl:grid-cols-[1.4fr_2.4rem_minmax(9rem,1fr)_5.5rem_5rem_5rem_2.5rem]';

export default function LeadPipelineTab() {
    const { getAuthHeader } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [stages, setStages] = useState([]);
    const [optionLists, setOptionLists] = useState({});
    const [confirmForce, setConfirmForce] = useState(null); // 409 detail payload
    // Where new form leads land (D5): a stage label, null for "none", undefined while unknown.
    const [placement, setPlacement] = useState(undefined);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const headers = getAuthHeader();
            const [res, definition] = await Promise.all([
                axios.get(`${API}/api/lead-pipeline/settings`, { headers }),
                // informational only: a failure just leaves the "land in" line out
                createPipelineDefinitionsApi(getAuthHeader).get('lead').catch(() => null),
            ]);
            // A stage saved before outcomes existed carries none: read its key default so a save keeps `won` / `lost`.
            setStages((res.data.stages || []).map(s => ({ ...s, outcome: leadStageOutcome(s) })));
            const lists = {};
            OPTION_LISTS.forEach(([k]) => { lists[k] = (res.data[k] || []).join(', '); });
            setOptionLists(lists);
            if (!definition) setPlacement(undefined);
            else if (definition.default_stage == null) setPlacement(null);
            else setPlacement((definition.stages || []).find(s => s.key === definition.default_stage)?.label || definition.default_stage);
        } catch (err) { toast.error(errorText(err, 'Failed to load settings')); }
        finally { setLoading(false); }
    }, [getAuthHeader]);

    useEffect(() => { load(); }, [load]);

    const updateStage = (i, patch) => setStages(prev => prev.map((s, idx) => idx === i ? { ...s, ...patch } : s));
    const moveStage = (i, dir) => setStages(prev => {
        const next = [...prev]; const j = i + dir;
        if (j < 0 || j >= next.length) return prev;
        [next[i], next[j]] = [next[j], next[i]];
        return next.map((s, idx) => ({ ...s, order: idx }));
    });
    const removeStage = (i) => setStages(prev => prev.filter((_, idx) => idx !== i).map((s, idx) => ({ ...s, order: idx })));
    const addStage = () => setStages(prev => [...prev, { key: '', label: '', color: '#64748b', order: prev.length, amber_days: 3, red_days: 7, ball_in_court: 'action_needed', outcome: 'open', _new: true }]);

    const buildPayload = () => {
        const usedKeys = new Set(stages.filter(s => !s._new).map(s => s.key));
        const cleanStages = stages.map((s, idx) => {
            let key = s.key;
            if (s._new) { key = stageKeyFromLabel(s.label, usedKeys); usedKeys.add(key); }
            return {
                key,
                label: s.label,
                color: s.color || '#64748b',
                order: idx,
                amber_days: (s.amber_days === '' || s.amber_days == null) ? null : Number(s.amber_days),
                red_days: (s.red_days === '' || s.red_days == null) ? null : Number(s.red_days),
                ball_in_court: s.ball_in_court || 'action_needed',
                outcome: leadStageOutcome({ ...s, key }),
            };
        });
        const payload = { stages: cleanStages };
        OPTION_LISTS.forEach(([k]) => {
            payload[k] = (optionLists[k] || '').split(',').map(x => x.trim()).filter(Boolean);
        });
        return payload;
    };

    const doSave = async (force = false) => {
        if (stages.some(s => !(s.label || '').trim())) { toast.error('Every stage needs a label'); return; }
        setSaving(true);
        try {
            await axios.put(`${API}/api/lead-pipeline/settings${force ? '?force=true' : ''}`, buildPayload(), { headers: getAuthHeader() });
            toast.success('Lead pipeline settings saved');
            setConfirmForce(null);
            load();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (err?.response?.status === 409 && Array.isArray(detail?.stages_in_use)) {
                setConfirmForce(detail);
            } else {
                toast.error(errorText(err, 'Failed to save'));
            }
        } finally { setSaving(false); }
    };

    if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-stone-400 animate-spin" /></div>;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-base font-semibold text-stone-800">Lead Pipeline</h3>
                    <p className="text-sm text-stone-500">Stages and dropdown options for the Lead Pipeline. Used on the lead record and the Lead Pipeline board.</p>
                    <p className="text-sm text-stone-500 mt-1" data-testid="lead-pipeline-versions-note">
                        Saving stages publishes a new version of the Lead pipeline · <Link to={HISTORY_LINK} className={linkCls}>View version history</Link>
                    </p>
                    {placement !== undefined && (
                        <p className="text-sm text-stone-500 mt-1" data-testid="lead-pipeline-placement">
                            New form leads land in: <b className="font-medium text-stone-700">{placement === null ? 'None' : placement}</b> · <Link to={PIPELINES_LINK} className={linkCls}>Change</Link>
                        </p>
                    )}
                </div>
                <Button onClick={() => doSave(false)} disabled={saving}>
                    {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-1.5" /> Save</>}
                </Button>
            </div>

            {/* Stages */}
            <div className="bg-white rounded-xl border border-stone-200">
                <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-stone-700">Stages</h4>
                    <Button variant="outline" size="sm" onClick={addStage}><Plus className="w-4 h-4 mr-1" /> Add stage</Button>
                </div>
                <div className="p-4 space-y-2">
                    <div className={`hidden xl:grid ${STAGE_GRID} gap-2 text-xs text-stone-400 px-1`}>
                        <span>Label</span><span>Color</span><span>Ball in court</span><span>Outcome</span><span>Amber d</span><span>Red d</span><span></span>
                    </div>
                    {stages.map((s, i) => (
                        <div key={s.key || `new-${i}`} className={`grid grid-cols-2 ${STAGE_GRID} gap-2 items-end xl:items-center`}>
                            <Field caption="Label">
                                <input className={inputCls} value={s.label} placeholder="Stage label" onChange={e => updateStage(i, { label: e.target.value })} />
                            </Field>
                            <Field caption="Color">
                                <input type="color" className="h-8 w-full rounded border border-stone-200 p-0.5" value={s.color || '#64748b'} onChange={e => updateStage(i, { color: e.target.value })} />
                            </Field>
                            <Field caption="Ball in court">
                                <select className={inputCls} value={s.ball_in_court || 'action_needed'} onChange={e => updateStage(i, { ball_in_court: e.target.value })}>
                                    {BALL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </Field>
                            <Field caption="Outcome">
                                <select className={inputCls} value={s.outcome} aria-label={`Outcome for ${s.key || `new stage ${i + 1}`}`} data-testid={`stage-outcome-${s.key || `new-${i}`}`}
                                    title="A Won or Lost stage never shows the Follow up badge, and dropping a card on it asks for the sale details or the reason lost"
                                    onChange={e => updateStage(i, { outcome: e.target.value })}>
                                    {LEAD_OUTCOME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </Field>
                            <Field caption="Amber d">
                                <input type="number" className={inputCls} value={s.amber_days ?? ''} placeholder="—" onChange={e => updateStage(i, { amber_days: e.target.value })} />
                            </Field>
                            <Field caption="Red d">
                                <input type="number" className={inputCls} value={s.red_days ?? ''} placeholder="—" onChange={e => updateStage(i, { red_days: e.target.value })} />
                            </Field>
                            <div className="flex items-center gap-0.5">
                                <button onClick={() => moveStage(i, -1)} className="p-1 text-stone-400 hover:text-stone-700" title="Move up"><ArrowUp className="w-3.5 h-3.5" /></button>
                                <button onClick={() => moveStage(i, 1)} className="p-1 text-stone-400 hover:text-stone-700" title="Move down"><ArrowDown className="w-3.5 h-3.5" /></button>
                                <button onClick={() => removeStage(i)} className="p-1 text-stone-400 hover:text-red-600" title="Remove"><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                        </div>
                    ))}
                    {stages.length === 0 && <p className="text-sm text-stone-400 py-4 text-center">No stages — add one.</p>}
                </div>
            </div>

            {/* Option lists */}
            <div className="bg-white rounded-xl border border-stone-200">
                <div className="px-4 py-3 border-b border-stone-100"><h4 className="text-sm font-semibold text-stone-700">Dropdown options</h4></div>
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {OPTION_LISTS.map(([k, label]) => (
                        <div key={k}>
                            <label className="block text-xs font-medium text-stone-600 mb-1">{label} <span className="text-stone-400">(comma-separated)</span></label>
                            <input className={`w-full ${inputCls}`} value={optionLists[k] || ''} onChange={e => setOptionLists(p => ({ ...p, [k]: e.target.value }))} />
                        </div>
                    ))}
                </div>
            </div>

            {/* Force-confirm when removing a stage that still has leads. Portaled
                to <body> (#496): ShellPageLayout's root is `.animate-fade-in`, whose
                transform makes it the containing block for an inline fixed overlay
                while the fade runs; inline it also took this root's `space-y-6`
                top margin. */}
            {confirmForce && createPortal(
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={() => setConfirmForce(null)}>
                    <div className="bg-white rounded-xl border border-stone-200 max-w-md w-full p-5" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-2 mb-2 text-amber-600"><AlertTriangle className="w-5 h-5" /><h4 className="font-semibold">Stages still in use</h4></div>
                        <p className="text-sm text-stone-600 mb-3">{confirmForce.message || 'Some removed stages still have leads.'}</p>
                        {Array.isArray(confirmForce.stages_in_use) && (
                            <ul className="text-sm text-stone-700 mb-4 list-disc pl-5">
                                {confirmForce.stages_in_use.map(s => <li key={s.key}><b>{s.key}</b> — {s.lead_count} lead{s.lead_count === 1 ? '' : 's'}</li>)}
                            </ul>
                        )}
                        <p className="text-xs text-stone-500 mb-4">Saving with force keeps those leads on a now-undefined stage (label/color won't resolve until reassigned).</p>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setConfirmForce(null)}>Cancel</Button>
                            <Button onClick={() => doSave(true)} disabled={saving}>Save anyway</Button>
                        </div>
                    </div>
                </div>,
                document.body,
            )}
        </div>
    );
}
